import { gql, useMutation, useQuery } from '@apollo/client'
import {
  Avatar,
  BasicTable,
  Button,
  Col,
  InputSearch,
  Loading,
  Row,
  Typography,
  WarningPopup,
  Tag,
  MUIActionButton
} from 'components'
import { convertUTCToLocal, formatCurrency, getFileUrl, isAdmin } from 'helpers/utils'
import { Helmet } from 'react-helmet-async'
import {
  DEAL_TYPES,
  STATUS_COLORS,
  ACTION_OPTIONS,
  USER_ROLES,
  AGENT_COLORS
} from 'helpers/constants'
import { GET_DEALS_REQUIRING_DEPOSIT_RECEIPTS } from 'helpers/gql'
import { useCallback, useContext, useState } from 'react'
import { UserContext } from 'context'
import { useNavigate } from 'react-router-dom'
import { usePopup } from 'hooks/usePopup'
import { toast } from 'react-toastify'
import styles from '../deals.module.scss'
import vars from '../../../vars.scss'
import { useMemo } from 'react'
import debounce from 'lodash.debounce'

const SORT_COLUMN_MAP = {
  agents: 'agents',
  price: 'sale_price',
  type: 'type',
  status_pretty_name: 'status_pretty_name',
  address: 'address',
  created: 'created',
  close_date: 'close_date',
  settled_date: 'settled_date',
  deposit_amount: 'deposit_amount'
}

const DepositReceipts = () => {
  const navigate = useNavigate()
  const { user } = useContext(UserContext)
  const [selectedRows, setSelectedRows] = useState([])
  const [confirmDelete, showConfirmDelete, hideConfirmDelete] = usePopup()
  const [checkedRows, setCheckedRows] = useState()

  // Server-side pagination, sorting, search state
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(50)
  const [sortBy, setSortBy] = useState('created')
  const [sortOrder, setSortOrder] = useState('desc')
  const [searchText, setSearchText] = useState('')

  const filters = useMemo(() => ({
    page,
    limit: rowsPerPage,
    sortBy,
    sortOrder,
    ...(searchText && { search: searchText })
  }), [page, rowsPerPage, sortBy, sortOrder, searchText])

  const {
    data: queryData,
    loading,
    refetch
  } = useQuery(GET_DEALS_REQUIRING_DEPOSIT_RECEIPTS, {
    fetchPolicy: 'no-cache',
    variables: { filters },
    onError: (err) => {
      toast.dismiss()
      if (!err?.message?.includes('Session timeout')) {
        toast.error(err.message)
      }
    }
  })

  const rows = queryData?.getDealsRequiringDepositReceipts?.data || []
  const meta = queryData?.getDealsRequiringDepositReceipts?.meta || {}

  const [deleteDeal, { loading: deleteLoading }] = useMutation(DELETE_DEAL, {
    onError: (err) => {
      toast.dismiss()
      if (!err?.message?.includes('Session timeout')) {
        toast.error(err.message)
      }
      hideConfirmDelete()
    },
    onCompleted: () => {
      toast.dismiss()
      setCheckedRows([])
      setSelectedRows([])
      toast.success('Deal(s) deleted.')
      refetch()
      hideConfirmDelete()
    }
  })

  const getActionOptions = (status) => {
    const canShowDeleteMenu =
      user.role === USER_ROLES.admin || user.role === USER_ROLES.super_admin

    const options = [
      { id: ACTION_OPTIONS.view, text: ACTION_OPTIONS.view },
      { id: ACTION_OPTIONS.edit, text: ACTION_OPTIONS.edit },
      canShowDeleteMenu && { id: ACTION_OPTIONS.delete, text: ACTION_OPTIONS.delete }
    ]
    return options.filter(Boolean)
  }

  const handleOnClickActionButton = (menu, publicId, type) => {
    if (ACTION_OPTIONS.view === menu) {
      navigate(`/deals/view/${type}/${publicId}`, { state: { from: 'deposit-receipts' } })
    } else if (ACTION_OPTIONS.edit === menu) {
      navigate(`/deals/edit/${type}/${publicId}`, { state: { from: 'deposit-receipts' } })
    } else if (ACTION_OPTIONS.delete === menu) {
      setSelectedRows([publicId])
      showConfirmDelete()
    }
  }

  const canDelete = useMemo(() => {
    return user.role === USER_ROLES.admin || user.role === USER_ROLES.super_admin
  }, [user.role])

  const columns = [
    {
      name: 'public_id',
      label: '',
      options: {
        display: false,
        sort: false
      }
    },
    {
      name: 'id',
      label: '',
      options: {
        display: false,
        sort: false
      }
    },
    {
      name: 'creator_id',
      label: '',
      options: {
        display: false,
        sort: false
      }
    },
    {
      name: 'agents',
      label: 'Agent',
      options: {
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const [public_id, id, creator_id] = tableMeta.rowData
          const dealAgent = value?.filter((a) => a.id === (isAdmin(user) ? creator_id : user.id))[0] || value?.[0]
          if (!dealAgent) return null
          return (
            <div className={styles.agentColumn}>
              <Avatar name={dealAgent.name} src={getFileUrl(dealAgent.thumbnail)} />
              <div>
                <Typography.Body className={styles.agentName}>{dealAgent.name}</Typography.Body>
                <Typography.Body style={{ color: vars['loading-inner-color-secondary'] }}>
                  {dealAgent.id === creator_id ? (
                    <Tag text={`Primary Rare Agent #${dealAgent.id}`} background={`${AGENT_COLORS['primary']}`} />
                  ) : (
                    <Tag text={`Secondary Rare Agent #${dealAgent.id}`} background={`${AGENT_COLORS['secondary']}`} />
                  )}
                </Typography.Body>
              </div>
            </div>
          )
        }
      }
    },
    {
      name: 'price',
      label: 'Price',
      options: {
        sort: true,
        customBodyRender: (value) => {
          return <Typography.Body style={{ color: vars['table-cell-color'] }}>{formatCurrency(value)}</Typography.Body>
        }
      }
    },
    {
      name: 'type',
      label: 'Deals',
      options: {
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const [public_id, id] = tableMeta.rowData
          const fontColor = tableMeta.rowData[14] === false ? 'red' : 'initial'
          const brokerageSide = tableMeta.rowData[6]
          const brokerageLabel = brokerageSide === 'listing' ? 'L' : brokerageSide === 'co-op' || brokerageSide === 'coop' ? 'C' : brokerageSide === 'both' ? 'B' : null
          const brokerageColor = brokerageSide === 'listing' ? '#1976d2' : brokerageSide === 'co-op' || brokerageSide === 'coop' ? '#ed6c02' : brokerageSide === 'both' ? '#9c27b0' : null
          const brokerageTitle = brokerageSide === 'listing' ? 'Listing' : brokerageSide === 'co-op' || brokerageSide === 'coop' ? 'Co-op' : brokerageSide === 'both' ? 'Both' : ''
          return (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Typography.Body style={{ color: fontColor }}>{DEAL_TYPES[value]}</Typography.Body>
                {brokerageLabel && (
                  <span
                    title={brokerageTitle}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '20px',
                      height: '20px',
                      borderRadius: '4px',
                      backgroundColor: brokerageColor,
                      color: '#fff',
                      fontSize: '11px',
                      fontWeight: 700,
                      lineHeight: 1
                    }}>
                    {brokerageLabel}
                  </span>
                )}
              </div>
              <Typography.Body style={{ color: fontColor }}>#{id}</Typography.Body>
            </>
          )
        }
      }
    },
    {
      name: 'brokerage_side',
      label: '',
      options: {
        display: false,
        sort: false
      }
    },
    {
      name: 'status',
      label: '',
      options: {
        display: false,
        sort: false
      }
    },
    {
      name: 'status_pretty_name',
      label: 'Status',
      options: {
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const status = tableMeta.rowData[7]
          return <Tag className={styles.statusColumn} text={value} background={`${STATUS_COLORS[status]}`} />
        }
      }
    },
    {
      name: 'address',
      label: 'Property Address',
      options: {
        sort: true,
        customBodyRender: (value) => {
          return <Typography.Body style={{ color: vars['table-cell-color'] }}>{value}</Typography.Body>
        }
      }
    },
    {
      name: 'created',
      label: 'Created Date',
      options: {
        sort: true,
        customBodyRender: (value) => {
          return (
            <Typography.Body style={{ color: vars['table-cell-color'] }}>
              {value ? convertUTCToLocal(value, 'MMM DD, yyyy hh:mm A') : null}
            </Typography.Body>
          )
        }
      }
    },
    {
      name: 'close_date',
      label: 'Closed Date',
      options: {
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const dealType = tableMeta.rowData[5]
          return (
            <Typography.Body style={{ color: vars['table-cell-color'] }}>
              {dealType === 'pre-con' ? (
                value ? (
                  convertUTCToLocal(value, 'yyyy')
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <span>-</span>
                  </div>
                )
              ) : value ? (
                convertUTCToLocal(value, 'MMM DD, yyyy hh:mm A')
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <span>-</span>
                </div>
              )}
            </Typography.Body>
          )
        }
      }
    },
    {
      name: 'settled_date',
      label: 'Settled Date',
      options: {
        sort: true,
        customBodyRender: (value) => {
          return (
            <Typography.Body style={{ color: vars['table-cell-color'] }}>
              {value ? convertUTCToLocal(value, 'MMM DD, yyyy hh:mm A') : <span>-</span>}
            </Typography.Body>
          )
        }
      }
    },
    {
      name: 'status',
      label: ' ',
      options: {
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const [public_id, id, creator_id, agents, sale_price, type] = tableMeta.rowData
          const actionOptions = getActionOptions(value)
          return (
            <MUIActionButton
              options={actionOptions}
              handleOnClickActionButton={(menu) => handleOnClickActionButton(menu, public_id, type)}
            />
          )
        }
      }
    },
    {
      name: 'checklist_tasks_completed',
      label: '',
      options: {
        display: false,
        sort: false
      }
    }
  ]

  // Server-side search with debounce
  const handleSearch = useCallback(
    debounce((searchedVal) => {
      setSearchText(searchedVal)
      setPage(1)
    }, 400),
    []
  )

  const handleRowSelect = useCallback(
    (currentRowsSelected, allRowsSelected, selectedRowIndices) => {
      const deletedRows = []
      allRowsSelected.map((row) => {
        const selectedRow = rows[row.index]
        return deletedRows.push(selectedRow?.public_id)
      })
      setSelectedRows(deletedRows.filter(Boolean))
      setCheckedRows(selectedRowIndices)
    },
    [rows]
  )

  const handleMultipleDelete = useCallback(() => {
    deleteDeal({
      variables: { id: selectedRows }
    })
  }, [deleteDeal, selectedRows])

  const handleConfirmDelete = () => {
    hideConfirmDelete()
    setCheckedRows([])
    setSelectedRows([])
  }

  // Map MUI DataTable column index to backend sortBy field
  const getColumnName = (index) => {
    const col = columns[index]
    return col?.name
  }

  const tableOptions = {
    print: false,
    pagination: true,
    elevation: 0,
    search: false,
    download: false,
    viewColumns: false,
    sort: true,
    filter: false,
    serverSide: true,
    count: meta.totalRows || 0,
    page: page - 1,
    rowsPerPage: rowsPerPage,
    rowsPerPageOptions: [10, 25, 50, 100],
    selectableRows: canDelete ? 'multiple' : 'none',
    selectableRowsHeader: false,
    rowsSelected: checkedRows,
    responsive: 'standard',
    onTableChange: (action, tableState) => {
      switch (action) {
        case 'changePage':
          setPage(tableState.page + 1)
          break
        case 'changeRowsPerPage':
          setRowsPerPage(tableState.rowsPerPage)
          setPage(1)
          break
        case 'sort':
          const columnName = getColumnName(tableState.activeColumn)
          const backendSortField = SORT_COLUMN_MAP[columnName] || columnName
          setSortBy(backendSortField)
          setSortOrder(tableState.announceText?.includes('ascending') ? 'asc' : 'desc')
          setPage(1)
          break
        default:
          break
      }
    },
    onRowSelectionChange: (currentRowsSelected, allRowsSelected, selectedRowIndices) => {
      handleRowSelect(currentRowsSelected, allRowsSelected, selectedRowIndices)
    }
  }

  return (
    <div className={styles.dealsTableMain}>
      <Helmet title="Deposit Receipts | RARE" />
      <section id="deposit-receipts">
        <Row alignItems="center" marginBottom="40px" justifyContent="space-between" className={styles.dealsDivMain}>
          <Col className={styles.dealsInnerDiv}>
            <InputSearch
              name="search"
              onChange={(e) => {
                handleSearch(e.target.value.trimStart())
              }}
            />
          </Col>
          <Col>
            <div style={{ display: 'flex' }}>
              {canDelete && (
                <Button
                  onClick={showConfirmDelete}
                  disabled={selectedRows?.length > 0 ? false : true}
                  style={{ marginLeft: '25px' }}>
                  Delete
                </Button>
              )}
            </div>
          </Col>
        </Row>
        {loading ? (
          <Loading fullpage />
        ) : (
          <BasicTable
            tableData={rows}
            tableColumns={columns}
            tableOptions={tableOptions}
          />
        )}
      </section>
      <WarningPopup
        openModel={confirmDelete}
        loading={deleteLoading}
        confirmBtnLabel={'Delete'}
        onCancle={handleConfirmDelete}
        onConfirm={handleMultipleDelete}>
        <Typography.Body type={1}>Do you really want to delete this deal(s)?</Typography.Body>
        {selectedRows.length > 1 && (
          <Typography.Body type={1} style={{ textAlign: 'center' }}>
            {selectedRows.length} deals selected
          </Typography.Body>
        )}
      </WarningPopup>
    </div>
  )
}

const DELETE_DEAL = gql`
  mutation deleteDeal($id: [String]!) {
    deleteDeal(ids: $id)
  }
`

export default DepositReceipts
